import React, { useState } from "react";

import {
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  SwapHoriz as SwapHorizIcon,
} from "@mui/icons-material";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Box,
  Typography,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";

import { useLegacyAuth } from "src/domains/auth/module/components/AuthProvider";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";
import { ALPHA } from "src/shell/styles/designTokens";

interface UserMenuProps {
  showLabel?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({ showLabel = false }) => {
  const theme = useTheme();
  const { user, logout } = useLegacyAuth();
  const appNavigation = useAppNavigation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleClose();
    appNavigation.goToProfile();
  };

  const handleSettingsClick = () => {
    handleClose();
    appNavigation.goToSettings();
  };

  const handleSwitchOrganizationClick = () => {
    handleClose();
    appNavigation.goToOrganizations();
  };

  const handleLogoutClick = () => {
    handleClose();
    logout();
  };

  const getUserDisplayName = () => {
    if (!user) return "User";

    if ("name" in user && user.name) {
      return user.name;
    }
    if ("firstName" in user && user.firstName) {
      return user.lastName
        ? `${user.firstName} ${user.lastName}`
        : user.firstName;
    }
    return "User";
  };

  const getUserInitial = () => {
    if (!user) return "U";

    if ("name" in user && user.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if ("firstName" in user && user.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    return "U";
  };

  if (!user) {
    return null;
  }

  return (
    <Box>
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: 40,
          px: showLabel ? 2 : 1,
          cursor: "pointer",
          color: theme.palette.text.secondary,
          "&:hover": {
            color: theme.palette.primary.main,
            backgroundColor: alpha(
              theme.palette.primary.main,
              ALPHA.NAV_ACTIVE_BG,
            ),
          },
          borderRadius: 1,
          transition: "color 0.2s ease, background-color 0.2s ease",
        }}
      >
        <Avatar
          sx={{
            width: showLabel ? 28 : 30,
            height: showLabel ? 28 : 30,
            fontSize: showLabel ? "0.75rem" : "0.75rem",
            fontWeight: showLabel ? 400 : 700,
            bgcolor: theme.palette.primary.main,
          }}
        >
          {getUserInitial()}
        </Avatar>
        {showLabel && (
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            {getUserDisplayName()}
          </Typography>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: theme.shadows[4],
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {getUserDisplayName()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </Box>

        <Divider />

        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Profile" />
        </MenuItem>

        <MenuItem onClick={handleSettingsClick}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </MenuItem>

        <MenuItem onClick={handleSwitchOrganizationClick}>
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Switch organization" />
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogoutClick}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserMenu;
